import { motion } from "framer-motion";
import {
  FileText,
  Zap,
  AlertTriangle,
  Palette,
  ChevronRight,
  Clock,
  Eye,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useEmailTemplates } from "@/hooks/use-email";
import { formatDistanceToNow } from "date-fns";

interface TemplateGridProps {
  onSelectTemplate: (templateId: string, content?: string) => void;
  selectedTemplate?: string;
}

// Default templates shown when no backend templates exist
const defaultTemplates = [
  {
    id: "daily_brief",
    name: "Daily Brief",
    description: "Quick summary of today's top stories with key insights.",
    category: "daily_brief" as const,
    icon: <FileText className="w-6 h-6" />,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    content: "",
    createdAt: new Date().toISOString(),
    lastUsed: undefined as string | undefined,
  },
  {
    id: "deep_dive",
    name: "Deep Dive",
    description: "In-depth analysis with detailed commentary and research.",
    category: "deep_dive" as const,
    icon: <Eye className="w-6 h-6" />,
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
    content: "",
    createdAt: new Date().toISOString(),
    lastUsed: undefined as string | undefined,
  },
  {
    id: "breaking_news",
    name: "Breaking News",
    description: "Urgent updates for time-sensitive stories and alerts.",
    category: "breaking_news" as const,
    icon: <AlertTriangle className="w-6 h-6" />,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    content: "",
    createdAt: new Date().toISOString(),
    lastUsed: undefined as string | undefined,
  },
  {
    id: "custom",
    name: "Custom",
    description: "Start from scratch with a blank canvas.",
    category: "custom" as const,
    icon: <Palette className="w-6 h-6" />,
    color: "text-violet-500",
    bgColor: "bg-violet-500/10",
    content: "",
    createdAt: new Date().toISOString(),
    lastUsed: undefined as string | undefined,
  },
];

// Icon mapping for templates from backend
const categoryIcons: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  daily_brief: {
    icon: <FileText className="w-6 h-6" />,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  deep_dive: {
    icon: <Eye className="w-6 h-6" />,
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
  },
  breaking_news: {
    icon: <AlertTriangle className="w-6 h-6" />,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  custom: {
    icon: <Palette className="w-6 h-6" />,
    color: "text-violet-500",
    bgColor: "bg-violet-500/10",
  },
};

function TemplateCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between mb-3">
          <Skeleton className="w-12 h-12 rounded-xl" />
          <Skeleton className="w-16 h-5 rounded-full" />
        </div>
        <Skeleton className="h-6 w-32 mb-2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mt-1" />
        <div className="flex items-center gap-1.5 mt-3">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="w-24 h-4" />
        </div>
      </div>
      <div className="px-5 pb-5">
        <Skeleton className="w-full h-[120px] rounded-lg" />
      </div>
    </Card>
  );
}

export function TemplateGrid({ onSelectTemplate, selectedTemplate }: TemplateGridProps) {
  const { data: backendTemplates, isLoading, isError } = useEmailTemplates();

  // Merge backend templates with defaults, preferring backend data
  const templates = backendTemplates?.length
    ? backendTemplates.map((t) => ({
        ...t,
        ...(categoryIcons[t.category] || categoryIcons.custom),
      }))
    : defaultTemplates;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Choose a Template</h2>
          <p className="text-sm text-muted-foreground">
            Select a template to start composing your newsletter
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <TemplateCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Choose a Template</h2>
        <p className="text-sm text-muted-foreground">
          Select a template to start composing your newsletter
        </p>
      </div>

      {isError && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-600 dark:text-amber-400">
          Could not load templates from server. Showing default templates.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {templates.map((template, index) => {
          const iconConfig = categoryIcons[template.category] || categoryIcons.custom;

          return (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              <Card
                className={cn(
                  "group relative overflow-hidden cursor-pointer transition-all duration-300",
                  "hover:shadow-lg hover:border-primary/30",
                  selectedTemplate === template.id &&
                    "ring-2 ring-primary border-primary"
                )}
                onClick={() => onSelectTemplate(template.category, template.content)}
              >
                {/* Header */}
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={cn("p-3 rounded-xl", iconConfig.bgColor)}>
                      <div className={iconConfig.color}>{iconConfig.icon}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {template.lastUsed && (
                        <Badge variant="secondary" className="text-xs">
                          Recently used
                        </Badge>
                      )}
                      <ChevronRight
                        className={cn(
                          "w-5 h-5 text-muted-foreground transition-transform duration-300",
                          "group-hover:translate-x-1 group-hover:text-primary"
                        )}
                      />
                    </div>
                  </div>

                  <h3 className="font-semibold text-lg mb-1">{template.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {template.description}
                  </p>

                  {template.lastUsed && (
                    <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      Last used {formatDistanceToNow(new Date(template.lastUsed), { addSuffix: true })}
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="px-5 pb-5">
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50 min-h-[120px]">
                    {template.content ? (
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none line-clamp-4 text-xs"
                        dangerouslySetInnerHTML={{
                          __html: template.content.substring(0, 200) + "...",
                        }}
                      />
                    ) : (
                      <TemplatePreviewPlaceholder category={template.category} />
                    )}
                  </div>
                </div>

                {/* Hover overlay */}
                <div
                  className={cn(
                    "absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent",
                    "opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                    "pointer-events-none"
                  )}
                />

                {/* Selected indicator */}
                {selectedTemplate === template.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                  >
                    <Zap className="w-3.5 h-3.5 text-primary-foreground" />
                  </motion.div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Quick tips */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-muted/30 rounded-lg p-4 border border-border/50"
      >
        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500" />
          Pro Tips
        </h4>
        <ul className="text-sm text-muted-foreground space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-primary">-</span>
            <span>
              <strong>Daily Brief</strong> works best for morning emails with quick insights
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">-</span>
            <span>
              Use <strong>Breaking News</strong> sparingly to maintain urgency impact
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary">-</span>
            <span>
              <strong>Deep Dive</strong> performs best on weekends when readers have more time
            </span>
          </li>
        </ul>
      </motion.div>
    </div>
  );
}

// Template preview placeholder based on category
function TemplatePreviewPlaceholder({ category }: { category: string }) {
  switch (category) {
    case "daily_brief":
      return (
        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h3 className="font-semibold text-xs">Today's Headlines</h3>
            <p className="text-xs text-muted-foreground">Quick overview of what matters</p>
          </div>
          <div className="space-y-2">
            <div className="h-2 bg-muted rounded w-full"></div>
            <div className="h-2 bg-muted rounded w-3/4"></div>
            <div className="h-2 bg-muted rounded w-5/6"></div>
          </div>
        </div>
      );
    case "deep_dive":
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20"></div>
            <div className="space-y-1">
              <div className="h-2 bg-muted rounded w-24"></div>
              <div className="h-2 bg-muted rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-2 bg-muted rounded w-full"></div>
            <div className="h-2 bg-muted rounded w-full"></div>
            <div className="h-2 bg-muted rounded w-4/5"></div>
          </div>
        </div>
      );
    case "breaking_news":
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 py-2 bg-red-500/10 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-xs font-medium text-red-500">BREAKING</span>
          </div>
          <div className="text-center space-y-2">
            <div className="h-3 bg-muted rounded w-3/4 mx-auto"></div>
            <div className="h-2 bg-muted rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      );
    case "custom":
    default:
      return (
        <div className="h-full flex flex-col items-center justify-center text-center space-y-3 py-6">
          <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Palette className="w-6 h-6 text-violet-500" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Blank Canvas</p>
            <p className="text-xs text-muted-foreground">Start fresh</p>
          </div>
        </div>
      );
  }
}
